import { motion, useInView } from 'framer-motion';
import { useEffect, useRef } from 'react';

const FAMEWALL_SCRIPT_ID = 'famewall-embed-script';
const FAMEWALL_SCRIPT_SRC = 'https://embed.famewall.io/frame.js';

const logoContext = require.context('../../assets/logos/clients', false, /\.png$/);
const logos = logoContext
  .keys()
  .sort((a, b) => {
    const aNumber = Number(a.replace('./', '').replace('.png', ''));
    const bNumber = Number(b.replace('./', '').replace('.png', ''));

    return aNumber - bNumber;
  })
  .map((filePath) => {
    const fileName = filePath.replace('./', '').replace('.png', '');

    return {
      name: `Client ${fileName}`,
      src: logoContext(filePath),
    };
  });

export const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  useEffect(() => {
    const existingScript = document.getElementById(FAMEWALL_SCRIPT_ID);

    if (existingScript) {
      return undefined;
    }

    const script = document.createElement('script');
    script.id = FAMEWALL_SCRIPT_ID;
    script.src = FAMEWALL_SCRIPT_SRC;
    script.type = 'text/javascript';
    script.defer = true;
    document.body.appendChild(script);

    return undefined;
  }, []);

  return (
    <section ref={ref} className="relative overflow-hidden py-24">
      <div className="absolute inset-0 bg-gradient-subtle" />
      <div className="absolute inset-0 bg-dots-pattern opacity-20" />

      <div className="section-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <span className="section-label mb-4 inline-block">
            Temoignages
          </span>
          <h2 className="mb-6 font-display text-3xl font-bold text-foreground sm:text-4xl lg:text-5xl">
            Ils nous font <span className="gradient-text">confiance</span>
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Decouvrez comment nous avons aide d&apos;autres entrepreneurs a structurer et developper leur activite.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="rounded-2xl border border-border/50 bg-card/40 p-4 shadow-lg backdrop-blur-sm sm:p-6">
            <div
              className="famewall-embed w-full"
              data-src="pulsia"
              data-format="slider"
              style={{ minHeight: '300px' }}
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-20"
        >
          <div className="mb-10 text-center">
            <span className="section-label mb-4 inline-block">
              Nos clients
            </span>
            <h3 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
              Ils ont fait confiance a <span className="gradient-text">PULSIA</span>
            </h3>
          </div>

          <div className="card-gradient relative overflow-hidden rounded-3xl border border-border/50 p-5 shadow-lg shadow-black/20 sm:p-7 lg:p-10">
            <div className="absolute -left-16 top-10 h-40 w-40 rounded-full bg-primary/10 blur-[70px]" />
            <div className="absolute -right-20 bottom-4 h-52 w-52 rounded-full bg-accent/10 blur-[90px]" />
            <div className="absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
            <div className="absolute bottom-0 left-0 top-0 z-10 w-16 bg-gradient-to-r from-card to-transparent sm:w-24" />
            <div className="absolute bottom-0 right-0 top-0 z-10 w-16 bg-gradient-to-l from-card to-transparent sm:w-24" />

            <div className="relative mb-8 flex flex-col gap-3 text-center sm:flex-row sm:items-end sm:justify-between sm:text-left">
              <div>
                <p className="text-sm font-medium uppercase tracking-[0.24em] text-primary">
                  References clients
                </p>
              </div>
              <div className="mx-auto h-px w-24 bg-gradient-to-r from-transparent via-border to-transparent sm:mx-0 sm:w-32" />
            </div>

            <div className="relative overflow-hidden py-2">
              <div className="flex w-max animate-marquee gap-4 [animation-duration:55s] hover:[animation-play-state:paused] sm:gap-5">
                {[...logos, ...logos].map((logo, index) => (
                  <div
                    key={`${logo.name}-${index}`}
                    className="group flex min-h-[124px] min-w-[150px] items-center justify-center rounded-2xl border border-white/10 bg-white/[0.055] p-4 shadow-inner shadow-white/[0.03] transition-all duration-300 hover:-translate-y-1 hover:border-primary/35 hover:bg-white/[0.09] hover:shadow-glow sm:min-h-[146px] sm:min-w-[174px] sm:p-5"
                  >
                    <img
                      src={logo.src}
                      alt={logo.name}
                      loading="lazy"
                      className="h-24 w-24 object-contain opacity-85 saturate-110 transition-all duration-300 group-hover:scale-110 group-hover:opacity-100 group-hover:brightness-110 sm:h-[7.5rem] sm:w-[7.5rem]"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="relative mt-4 overflow-hidden py-2">
              <div className="flex w-max animate-marquee gap-4 [animation-direction:reverse] [animation-duration:62s] hover:[animation-play-state:paused] sm:gap-5">
                {[...logos.slice().reverse(), ...logos.slice().reverse()].map((logo, index) => (
                  <div
                    key={`${logo.name}-reverse-${index}`}
                    className="group flex min-h-[124px] min-w-[150px] items-center justify-center rounded-2xl border border-white/10 bg-white/[0.045] p-4 shadow-inner shadow-white/[0.03] transition-all duration-300 hover:-translate-y-1 hover:border-primary/35 hover:bg-white/[0.09] hover:shadow-glow sm:min-h-[146px] sm:min-w-[174px] sm:p-5"
                  >
                    <img
                      src={logo.src}
                      alt={logo.name}
                      loading="lazy"
                      className="h-24 w-24 object-contain opacity-85 saturate-110 transition-all duration-300 group-hover:scale-110 group-hover:opacity-100 group-hover:brightness-110 sm:h-[7.5rem] sm:w-[7.5rem]"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
